import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  ShoppingCart,
  MapPin,
  Paperclip,
  Search,
  Loader2,
  CreditCard,
  ExternalLink
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, useToast } from '@caruso/ui';
import { cn } from '@caruso/ui';
import { productsService, Product } from '@/services/products.service';
import { venuesService } from '@/services/venues.service';
import { Venue } from '@caruso/types';

type OptionType = 'menu' | 'product' | 'venue' | 'file';

interface AttachmentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendPaymentLink: (product: Product) => void;
  onSendVenueLink: (venue: Venue) => void;
  onSelectFile: () => void;
}

export function AttachmentOptionsModal({
  isOpen,
  onClose,
  onSendPaymentLink,
  onSendVenueLink,
  onSelectFile,
}: AttachmentOptionsModalProps) {
  const [currentView, setCurrentView] = useState<OptionType>('menu');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  // Buscar produtos
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', 'active', searchQuery],
    queryFn: () => productsService.getAll({ activeOnly: true, search: searchQuery, limit: 50 }),
    enabled: currentView === 'product',
  });

  // Buscar ambientes
  const { data: venues, isLoading: isLoadingVenues } = useQuery({
    queryKey: ['venues', 'active'],
    queryFn: () => venuesService.getAll(true),
    enabled: currentView === 'venue',
  });

  const products = productsData?.data || [];

  const handleBack = () => {
    setCurrentView('menu');
    setSearchQuery('');
  };

  const handleSelectProduct = (product: Product) => {
    if (!product.price) {
      toast({
        title: 'Produto sem preco',
        description: 'Este produto nao tem preco definido.',
        variant: 'destructive',
      });
      return;
    }
    onSendPaymentLink(product);
    handleClose();
  };

  const handleSelectVenue = (venue: Venue) => {
    onSendVenueLink(venue);
    handleClose();
  };

  const handleClose = () => {
    setCurrentView('menu');
    setSearchQuery('');
    onClose();
  };

  const handleFileOption = () => {
    onSelectFile();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-brand-ink border border-brand-gold/20 rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
            <h2 className="text-lg font-serif text-brand-gold">
              {currentView === 'menu' && 'Enviar'}
              {currentView === 'product' && 'Selecionar Produto'}
              {currentView === 'venue' && 'Selecionar Ambiente'}
            </h2>
            <div className="flex items-center gap-2">
              {currentView !== 'menu' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="text-brand-ivory/70 hover:text-brand-gold"
                >
                  Voltar
                </Button>
              )}
              <button
                onClick={handleClose}
                className="p-1 rounded-lg hover:bg-brand-gold/10 text-brand-ivory/50 hover:text-brand-gold transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {/* Menu View */}
            {currentView === 'menu' && (
              <div className="space-y-2">
                <button
                  onClick={() => setCurrentView('product')}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-brand-ivory group-hover:text-brand-gold transition-colors">
                      Link de Pagamento
                    </p>
                    <p className="text-sm text-brand-ivory/50">
                      Envie um link para venda rapida de um produto
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setCurrentView('venue')}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-brand-ivory group-hover:text-brand-gold transition-colors">
                      Link de Ambiente
                    </p>
                    <p className="text-sm text-brand-ivory/50">
                      Envie o link de um ambiente para reserva
                    </p>
                  </div>
                </button>

                <button
                  onClick={handleFileOption}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors group"
                >
                  <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
                    <Paperclip className="w-6 h-6 text-brand-gold" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-brand-ivory group-hover:text-brand-gold transition-colors">
                      Arquivo ou Imagem
                    </p>
                    <p className="text-sm text-brand-ivory/50">
                      Envie uma imagem ou documento
                    </p>
                  </div>
                </button>
              </div>
            )}

            {/* Product Selection */}
            {currentView === 'product' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
                  <input
                    type="text"
                    placeholder="Buscar produto..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg text-brand-ivory placeholder:text-brand-ivory/40 focus:outline-none focus:border-brand-gold/30 text-sm"
                  />
                </div>

                {/* Product List */}
                {isLoadingProducts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-8 text-brand-ivory/50">
                    <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhum produto encontrado</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {products.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => handleSelectProduct(product)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors group"
                      >
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0].url}
                            alt={product.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-brand-smoke flex items-center justify-center">
                            <ShoppingCart className="w-5 h-5 text-brand-ivory/30" />
                          </div>
                        )}
                        <div className="flex-1 text-left">
                          <p className="font-medium text-brand-ivory group-hover:text-brand-gold transition-colors truncate">
                            {product.name}
                          </p>
                          <p className="text-sm text-green-400">
                            {product.price
                              ? `R$ ${product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                              : 'Sem preco'}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-brand-ivory/30 group-hover:text-brand-gold transition-colors" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Venue Selection */}
            {currentView === 'venue' && (
              <div className="space-y-4">
                {isLoadingVenues ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
                  </div>
                ) : !venues || venues.length === 0 ? (
                  <div className="text-center py-8 text-brand-ivory/50">
                    <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhum ambiente encontrado</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {venues.map((venue) => (
                      <button
                        key={venue.id}
                        onClick={() => handleSelectVenue(venue)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-brand-smoke/30 hover:bg-brand-gold/10 transition-colors group"
                      >
                        {venue.images?.[0] ? (
                          <img
                            src={venue.images[0].url}
                            alt={venue.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-brand-smoke flex items-center justify-center">
                            <MapPin className="w-5 h-5 text-brand-ivory/30" />
                          </div>
                        )}
                        <div className="flex-1 text-left">
                          <p className="font-medium text-brand-ivory group-hover:text-brand-gold transition-colors truncate">
                            {venue.name}
                          </p>
                          <p className="text-sm text-brand-ivory/50 truncate">
                            {venue.description || 'Sem descricao'}
                          </p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-brand-ivory/30 group-hover:text-brand-gold transition-colors" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
